/**
 * Transaction confirmation with WebSocket primary + HTTP polling fallback.
 *
 * Relocated from frontend/src/lib/tx-engine/confirmation.ts as part of the
 * auth boundary migration (spec 009 FILE 14 Group E). The frontend version
 * registered a `document.visibilitychange` listener directly — forbidden in
 * SDK core (.claude/rules/platform-agnostic.md). Instead the foreground-
 * resume signal is injected via the optional `TxOnForeground` slot
 * (web wires `adapter.appState`; RN wires `AppState`).
 *
 * Platform-neutral — safe in src/auth/ core.
 */

import type { Connection } from '@solana/web3.js';

import type { TxEngineLogger, TxOnForeground } from '../config.js';
import { createTxError, TxErrorCode } from '../errors/errors.js';
import { extractMorfiErrorName } from '../errors/parse-solana-error.js';
import type { ConfirmationResult, LandingConfig } from '../types.js';

const HTTP_POLL_GRACE_MS = 2_000;
const HTTP_POLL_INTERVAL_MS = 2_000;
const BLOCK_HEIGHT_CHECK_MS = 10_000;

/** Commitment levels ranked by finality. Higher index = stronger guarantee. */
const COMMITMENT_RANK: Record<string, number> = { processed: 0, confirmed: 1, finalized: 2 };

/** Bounded confirmation facts consumed by the terminal lifecycle owner. */
export interface ConfirmationDiagnostics {
  readonly wsSubscriptionFallback: boolean;
  readonly pollingTransportFailures: number;
  readonly expiryStatusLookups: number;
  readonly blockHeightTransportFailures: number;
}

/** Internal enrichment stripped before the transaction engine's public result. */
export type InternalConfirmationResult = ConfirmationResult & {
  readonly diagnostics: ConfirmationDiagnostics;
};

/** Check if an observed commitment level meets or exceeds the required level. */
function meetsCommitment(
  observed: string | null | undefined,
  required: 'processed' | 'confirmed' | 'finalized',
): boolean {
  if (!observed) return false;
  const observedRank = COMMITMENT_RANK[observed] ?? -1;
  /* v8 ignore start -- `required` is a fixed union; `?? 3` is a noUncheckedIndexedAccess guard */
  const requiredRank = COMMITMENT_RANK[required] ?? 3;
  /* v8 ignore stop */
  return observedRank >= requiredRank;
}

/**
 * Confirm a transaction using WebSocket (primary) + HTTP polling (fallback).
 * WebSocket disconnect silently falls back to HTTP polling.
 * On foreground-resume (tab/app returns), immediately checks status.
 * Signature-only observers omit blockhashInfo; expiry cannot be inferred without
 * the lifetime of the signed transaction, so they remain bounded by the timeout.
 */
export async function confirmTransaction(
  connection: Pick<
    Connection,
    'onSignature' | 'removeSignatureListener' | 'getSignatureStatuses' | 'getBlockHeight'
  >,
  signature: string,
  blockhashInfo: { blockhash: string; lastValidBlockHeight: number } | undefined,
  config: LandingConfig,
  traceId: string,
  _logger: TxEngineLogger,
  onForeground?: TxOnForeground,
  signal?: AbortSignal,
  programIdHint?: string,
): Promise<InternalConfirmationResult> {
  // `getSignatureStatuses` returns an `err` WITHOUT logs, so the parser can't
  // attribute the markets/cp-swap 6000+ overlap from a log line. For
  // graduated-AMM swaps the hook passes the cp-swap id so cp-swap codes resolve
  // to the right name. See ExtractMorfiErrorOptions in parse-solana-error.ts.
  const hintOpts = programIdHint !== undefined ? { programIdHint } : undefined;
  return new Promise<InternalConfirmationResult>((resolve) => {
    let resolved = false;
    let wsSubscriptionFallback = false;
    let pollingTransportFailures = 0;
    let expiryStatusLookups = 0;
    let blockHeightTransportFailures = 0;
    let wsSubscriptionId: number | undefined;
    let pollTimerId: ReturnType<typeof setTimeout> | undefined;
    let pollIntervalId: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let heightCheckId: ReturnType<typeof setInterval> | undefined;
    let foregroundUnsub: (() => void) | undefined;

    // Declared early so cleanup can reference it; assigned after definition
    let onAbort: (() => void) | undefined;

    const cleanup = (): void => {
      if (wsSubscriptionId !== undefined) {
        try {
          connection.removeSignatureListener(wsSubscriptionId);
        } catch {
          // Cleanup cannot change an already-settled confirmation outcome.
        }
        wsSubscriptionId = undefined;
      }
      if (pollTimerId !== undefined) {
        clearTimeout(pollTimerId);
        pollTimerId = undefined;
      }
      if (pollIntervalId !== undefined) {
        clearInterval(pollIntervalId);
        pollIntervalId = undefined;
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      if (heightCheckId !== undefined) {
        clearInterval(heightCheckId);
        heightCheckId = undefined;
      }
      if (foregroundUnsub) {
        foregroundUnsub();
        foregroundUnsub = undefined;
      }
      if (signal && onAbort) {
        signal.removeEventListener('abort', onAbort);
      }
    };

    const confirmationDiagnostics = (): ConfirmationDiagnostics => ({
      wsSubscriptionFallback,
      pollingTransportFailures,
      expiryStatusLookups,
      blockHeightTransportFailures,
    });

    function finish(result: ConfirmationResult): void {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve({ ...result, diagnostics: confirmationDiagnostics() });
    }

    // Handle abort signal
    if (signal?.aborted) {
      finish({ status: 'cancelled' });
      return;
    }

    onAbort = (): void => {
      finish({ status: 'cancelled' });
    };

    if (signal) {
      signal.addEventListener('abort', onAbort, { once: true });
    }

    // Overall timeout
    timeoutId = setTimeout(() => {
      finish({ status: 'timeout' });
    }, config.confirmationTimeoutMs);

    // Settle from an observed on-chain status — shared by the poll loop and
    // the expiry check. On-chain error → failed; commitment met → confirmed;
    // otherwise the watch keeps running (the status will progress).
    function settleFromStatus(status: {
      err: unknown;
      confirmationStatus?: string | null;
      slot: number;
    }): void {
      if (status.err) {
        const morfiError = extractMorfiErrorName(status.err, hintOpts);
        finish({
          status: 'failed',
          error: createTxError(TxErrorCode.TX_CONFIRM_FAILED, traceId, 'confirming', {
            ...(morfiError !== undefined ? { morfiError } : {}),
          }),
        });
        return;
      }

      // Commitment levels are hierarchical: processed < confirmed < finalized.
      // Accept any level >= the requested commitment.
      if (meetsCommitment(status.confirmationStatus, config.commitment)) {
        finish({ status: 'confirmed', slot: status.slot });
      }
    }

    // HTTP polling function
    async function pollStatus(): Promise<void> {
      /* v8 ignore start -- defensive re-entry guard: cleanup() clears the poll timers on finish() */
      if (resolved) return;
      /* v8 ignore stop */

      try {
        const statuses = await connection.getSignatureStatuses([signature]);
        const status = statuses.value[0];

        if (!status) return;
        settleFromStatus(status);
      } catch {
        pollingTransportFailures++;
      }
    }

    // Start HTTP polling after grace period
    pollTimerId = setTimeout(() => {
      pollStatus();
      pollIntervalId = setInterval(pollStatus, HTTP_POLL_INTERVAL_MS);
    }, HTTP_POLL_GRACE_MS);

    // Foreground-resume handler (EC-9) — injected, platform-neutral.
    if (onForeground) {
      foregroundUnsub = onForeground(() => {
        if (!resolved) {
          pollStatus();
        }
      });
    }

    // WebSocket confirmation (primary)
    try {
      wsSubscriptionId = connection.onSignature(
        signature,
        (result, context) => {
          if (resolved) return;

          if (result.err) {
            const morfiError = extractMorfiErrorName(result.err, hintOpts);
            finish({
              status: 'failed',
              error: createTxError(TxErrorCode.TX_CONFIRM_FAILED, traceId, 'confirming', {
                ...(morfiError !== undefined ? { morfiError } : {}),
              }),
            });
            return;
          }

          finish({ status: 'confirmed', slot: context.slot });
        },
        config.commitment,
      );
    } catch {
      wsSubscriptionFallback = true;
    }

    // Check blockhash expiry via block height
    const checkBlockHeight = async (): Promise<void> => {
      /* v8 ignore start -- defensive re-entry guard: cleanup() clears the height-check interval on finish() */
      if (resolved || blockhashInfo === undefined) return;
      /* v8 ignore stop */
      try {
        const currentHeight = await connection.getBlockHeight('confirmed');
        if (currentHeight <= blockhashInfo.lastValidBlockHeight) return;

        // Height past `lastValidBlockHeight` only proves the tx can no longer
        // land ANEW — it may have ALREADY landed while the WS/poll signals
        // missed it (a slow first Turnkey sign eats most of the blockhash
        // window; a flaky RPC drops the polls). The submit pipeline reacts to
        // `expired` by RE-SIGNING and RE-RELAYING the same trade with a fresh
        // blockhash, so declaring expiry without checking double-executes the
        // trade on chain (MOR-856: the first trade landed twice). One
        // authoritative history-backed status lookup decides landed vs expired.
        expiryStatusLookups++;
        const statuses = await connection.getSignatureStatuses([signature], {
          searchTransactionHistory: true,
        });
        /* v8 ignore start -- defensive re-entry guard: another signal can settle while the lookup is in flight */
        if (resolved) return;
        /* v8 ignore stop */
        const status = statuses.value[0];
        if (status) {
          // Landed: settle from the real outcome. A status still below the
          // required commitment leaves the watch running — the poll loop
          // (or the next height check) picks it up once it progresses.
          settleFromStatus(status);
          return;
        }

        finish({ status: 'expired' });
      } catch {
        // Also guards the status lookup: on an RPC error we do NOT declare
        // expiry this tick — the next interval retries, the overall timeout
        // still bounds the wait, and a false `expired` is the dangerous case.
        blockHeightTransportFailures++;
      }
    };

    if (blockhashInfo !== undefined) {
      heightCheckId = setInterval(() => {
        checkBlockHeight();
      }, BLOCK_HEIGHT_CHECK_MS);
    }
  });
}
